import { useCallback, useEffect, useState } from "react";
import { IonSkeletonText } from "@ionic/react";
import { useTranslation } from 'react-i18next';
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import UserUtil from "../../utilities/user-util";
import OrganizationUtil, { AvailableLimits } from "../../utilities/organization-util";
import OrganizationService from "../../utilities/services/organization-service";
import { createLogger } from "../../utilities/logger";
import { Organization } from "../../models/interfaces/organization";
import { Button } from "../button/button";
import { SummaryTile } from "../summary-tile/summary-tile";
import PortalProgressBar from "../portal-progress-bar/portal-progress-bar";
import { EditOrganizationModal } from "../organization-management/edit-organization-modal/edit-organization-modal";
import { SubOrganizationModal } from "../organization-management/sub-organization-modal/sub-organization-modal";
import { OrganizationListTable } from "./organization-list-table";

const log = createLogger("SubOrganizationList");

const COMPONENT_CLASS = "c-organization-list";

type AllocatedLimits = { subOrganizationCount: number; userLimit: number; licenseLimit: number };

/**
 * Placeholder tile shown while the head organization's capacity loads.
 * Mirrors the real tile structure so the page doesn't shift when data lands.
 */
const OrganizationTileSkeleton: React.FC = () => (
    <SummaryTile
        tone="white"
        padding="compact"
        className={`${COMPONENT_CLASS}__tile`}>
        <div className={`${COMPONENT_CLASS}__tile-header`} aria-hidden="true">
            <IonSkeletonText animated className={`${COMPONENT_CLASS}__tile-skeleton-title`} />
            <IonSkeletonText animated className={`${COMPONENT_CLASS}__tile-skeleton-subtitle`} />
        </div>
        <div className={`${COMPONENT_CLASS}__tile-progress`} aria-hidden="true">
            <div className={`${COMPONENT_CLASS}__tile-progress-header`}>
                <IonSkeletonText animated className={`${COMPONENT_CLASS}__tile-skeleton-number`} />
                <IonSkeletonText animated className={`${COMPONENT_CLASS}__tile-skeleton-caption`} />
            </div>
            <IonSkeletonText animated className={`${COMPONENT_CLASS}__tile-skeleton-bar`} />
        </div>
    </SummaryTile>
);

/**
 * Org Mgmt for account holders: lists only the sub-organizations of the head
 * organization and surfaces the head's remaining capacity that can still be
 * allocated.
 */
export const SubOrganizationList: React.FC = () => {
    const { state } = useAuthState();
    const { t } = useTranslation();

    const [editOrgId, setEditOrgId] = useState<string>();
    const [openEdit, setOpenEdit] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [algoliaFilters, setAlgoliaFilters] = useState<string>(`organizationId:"NO_MATCH"`);
    const [limitReached, setLimitReached] = useState<boolean>(false);
    const [tilesLoading, setTilesLoading] = useState<boolean>(true);
    const [headOrg, setHeadOrg] = useState<Organization>();
    const [availableLimits, setAvailableLimits] = useState<AvailableLimits>();
    const [allocatedLimits, setAllocatedLimits] = useState<AllocatedLimits>();

    useEffect(() => {
        const getFilters = async () => {
            setAlgoliaFilters(await UserUtil.getAlgoliaSubOrganizationFilter(state.user));
        };
        getFilters();
    }, [state.user]);

    const loadHeadOrganization = useCallback(async () => {
        try {
            if (!state.user?.organizationId) {
                return;
            }

            let headOrg;

            if (state.user.organizationId === state.organization.id) {
                headOrg = await OrganizationService.get(state.organization.id);
            } else if (state.organization.parentOrganizationId) {
                headOrg = await OrganizationService.get(state.organization.parentOrganizationId);
            } else {
                return;
            }

            if (!headOrg) {
                return;
            }

            setHeadOrg(headOrg);
            setLimitReached(OrganizationUtil.isSubOrganizationLimitReached(headOrg));

            const [available, allocated] = await Promise.all([
                OrganizationUtil.getAvailableLimits(headOrg),
                OrganizationUtil.getSubOrganizationAllocation(headOrg),
            ]);

            setAvailableLimits(available);
            setAllocatedLimits(allocated);
        } catch (e) {
            log.error("Failed to load head organization capacity", {
                exception: e,
                organizationId: state.organization?.id,
            });
        } finally {
            setTilesLoading(false);
        }
    }, [state.user, state.organization.id, state.organization.parentOrganizationId]);

    useEffect(() => {
        loadHeadOrganization();
    }, [loadHeadOrganization]);

    const userSeatPool = (allocatedLimits?.userLimit ?? 0) + (availableLimits?.userLimit ?? 0);
    const wellnessPool = (allocatedLimits?.licenseLimit ?? 0) + (availableLimits?.licenseLimit ?? 0);

    return (
        <div className={`${COMPONENT_CLASS}`}>
            <div className={`${COMPONENT_CLASS}__header`}>
                <div className={`${COMPONENT_CLASS}__actions`}>
                    <Button
                        type="default"
                        onClick={() => setOpenAdd(true)}
                        buttonText={limitReached ? t('buttons.btn_subOrgLimitReached') : t('buttons.btn_addSubOrg')}
                        disabled={limitReached}
                    />
                </div>
                <h1>{t('organization.edit.fields.subOrganizations')}</h1>
            </div>
            {(tilesLoading || (availableLimits && allocatedLimits)) &&
                <div className={`${COMPONENT_CLASS}__tiles`}>
                    {availableLimits && allocatedLimits
                        ? (
                            <>
                                <SummaryTile
                                    tone="white"
                                    padding="compact"
                                    className={`${COMPONENT_CLASS}__tile`}>
                                    <div className={`${COMPONENT_CLASS}__tile-header`}>
                                        <h1 className={`label-large ${COMPONENT_CLASS}__tile-title`}>{t('organization.tiles.userSeats')}</h1>
                                        <p className={`small-text ${COMPONENT_CLASS}__tile-subtitle`}>
                                            {t('organization.tiles.allocationSummary', {
                                                allocated: allocatedLimits.userLimit,
                                                count: allocatedLimits.subOrganizationCount,
                                            })}
                                        </p>
                                    </div>
                                    {userSeatPool > 0 &&
                                        <div className={`${COMPONENT_CLASS}__tile-progress`}>
                                            <PortalProgressBar
                                                total={userSeatPool}
                                                currentProgress={allocatedLimits.userLimit}
                                                headerContent={
                                                    <div className={`${COMPONENT_CLASS}__tile-progress-header`}>
                                                        <span className="data-medium">{availableLimits.userLimit}</span>
                                                        <span className="small-text">{t('organization.tiles.availableToAllocate')}</span>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    }
                                </SummaryTile>
                                <SummaryTile
                                    tone="white"
                                    padding="compact"
                                    className={`${COMPONENT_CLASS}__tile`}>
                                    <div className={`${COMPONENT_CLASS}__tile-header`}>
                                        <h1 className={`label-large ${COMPONENT_CLASS}__tile-title`}>{t('organization.tiles.wellnessPrograms')}</h1>
                                        <p className={`small-text ${COMPONENT_CLASS}__tile-subtitle`}>
                                            {t('organization.tiles.allocationSummary', {
                                                allocated: allocatedLimits.licenseLimit,
                                                count: allocatedLimits.subOrganizationCount,
                                            })}
                                        </p>
                                    </div>
                                    {wellnessPool > 0 &&
                                        <div className={`${COMPONENT_CLASS}__tile-progress`}>
                                            <PortalProgressBar
                                                total={wellnessPool}
                                                currentProgress={allocatedLimits.licenseLimit}
                                                headerContent={
                                                    <div className={`${COMPONENT_CLASS}__tile-progress-header`}>
                                                        <span className="data-medium">{availableLimits.licenseLimit}</span>
                                                        <span className="small-text">{t('organization.tiles.availableToAllocate')}</span>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    }
                                </SummaryTile>
                            </>
                        )
                        : (
                            <>
                                <OrganizationTileSkeleton />
                                <OrganizationTileSkeleton />
                            </>
                        )
                    }
                </div>
            }
            <OrganizationListTable
                key={listRefreshKey}
                algoliaFilters={algoliaFilters}
                showPlanStatus={false}
                onEdit={(h) => {
                    setEditOrgId(h.id);
                    setOpenEdit(true);
                }}
                onDeleted={() => loadHeadOrganization()}
            />
            <EditOrganizationModal
                open={openEdit}
                onClose={() => {
                    setOpenEdit(false);
                    loadHeadOrganization();
                }}
                organizationId={editOrgId}
                parentOrganization={headOrg}
                availableLimits={availableLimits}
            />
            <SubOrganizationModal
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                title={t('organization.edit.addSubOrganization')}
                parentOrganization={headOrg}
                availableLimits={availableLimits}
                onSaved={() => {
                    setOpenAdd(false);
                    setListRefreshKey((key) => key + 1);
                    loadHeadOrganization();
                }}
            />
        </div>
    );
};

export default SubOrganizationList;
